/**
 * Fighting with whatever's in your hands. Anything that isn't a weapon can still go in the
 * weapon slot and is better than fists: a hatchet or a pickaxe nearly as good as a sword of its
 * level would be (but slower), a harpoon like a spear, a hammer heavy, a log a club, a fish
 * a fish. A tool you're holding still works as that tool: woodcutting with the hatchet in your
 * hand works just the same.
 */
import { GEAR, setImprovised, type GearDef, type GearId } from './content/equipment.js';
import { ITEMS, isItemId, type ItemId } from './content/items.js';
import { TOOLS, isToolId, toolDef } from './content/tools.js';

const cache = new Map<string, GearDef>();
/** Tools that are containers: their contents live with them, so they stay in the pack. */
const NOT_SWUNG = new Set(['sack', 'basket', 'barrel']);

/** Can this go in the weapon slot? (Weapons, and almost anything else that isn't a stack.) */
export function canWield(id: string): boolean {
  if (Object.prototype.hasOwnProperty.call(GEAR, id)) return true;
  if (!isItemId(id) || ITEMS[id].stack || ITEMS[id].cat === 'ammo') return false;
  return !(isToolId(id) && NOT_SWUNG.has(toolDef(id).line));
}
/** Is it a real weapon, or something pressed into service? */
export const isImprovised = (id: string) => !Object.prototype.hasOwnProperty.call(GEAR, id) && canWield(id);

/** The fighting stats of a non-weapon. */
export function improvised(id: string): GearDef | null {
  const hit = cache.get(id); if (hit) return hit;
  if (!isImprovised(id)) return null;
  const item = ITEMS[id as ItemId];
  let acc = 1, str = 1, speed = 4, desc = 'Not a weapon, but better than your fists.';
  let metal: GearDef['metal'] = 5;
  if (isToolId(id)) {
    const t = toolDef(id), L = t.use[1] + 6;
    metal = (t.metal && t.metal > 0 ? t.metal : 1) as GearDef['metal'];
    switch (t.line) {
      case 'axe': case 'pick': acc = 0.6 * L; str = 0.6 * L; speed = 5; desc = `A ${t.line === 'axe' ? 'hatchet' : 'pickaxe'} makes a fair weapon: nearly a sword's bite, but slower.`; break;
      case 'harpoon': acc = 0.6 * L; str = 0.5 * L; speed = 4; desc = 'It spears more than fish.'; break;
      case 'hammer': acc = 3; str = 5; speed = 5; desc = 'Heavy in the hand.'; break;
      case 'chisel': acc = 4; str = 2; speed = 3; desc = 'Quick little jabs.'; break;
      default: acc = 2; str = 2; speed = 5;
    }
    if (t.broken) { acc = 1; str = 1; }
  } else if (item.cat === 'log' || item.cat === 'bar') { acc = 2; str = 3; speed = 5; desc = item.cat === 'log' ? 'A club, more or less.' : 'A heavy bar of metal: hits hard, swings slow.'; }
  const d: GearDef = { name: item.name, slot: 'weapon', req: ['melee', 1], acc: Math.max(1, Math.round(acc)), str: Math.max(1, Math.round(str)), speed, range: 1, style: 'melee', metal, desc };
  cache.set(id, d);
  return d;
}
setImprovised(improvised);
/** What the player model holds when fighting with it. */
export function heldFor(id: string): 'axe' | 'pick' | 'rod' | 'net' | 'harpoon' | 'hammer' | 'trap' | 'knife' | 'club' | 'item' {
  if (isToolId(id)) {
    const t = TOOLS[id];
    if (t.held && !String(t.held).startsWith('gear:')) return t.held as 'axe';
    return t.line === 'chisel' ? 'knife' : 'item';
  }
  return ITEMS[id as ItemId]?.cat === 'log' ? 'club' : 'item';
}
export type { GearId };
